/**
 * Predictive results under the header search bar.
 *
 * The bar is a plain form and stays one: submitting it goes to /search, with
 * or without this script. What this adds is the dropdown, rendered under the
 * bar instead of behind a full-screen overlay, because a bar that opens an
 * overlay is just a button wearing an input's clothes.
 *
 * The results come from our own search rather than Shopify's, because Shopify's
 * ranks a £1 shipping line above the suits. They are drawn as a grid of cards
 * on anything wider than a phone and as rows on a phone — a clothes search is
 * decided on the photograph, and a column of names with the picture shrunk to
 * a stamp is not a way to choose between two navy suits.
 *
 * Nothing here is required for search to work. Every failure path — a dead
 * network, an unparseable response — leaves the form exactly as it was.
 */
(function () {
  'use strict';

  var MIN_CHARS = 2;
  /* Long enough to skip the letters of a word typed in one go, short enough
     not to be felt. It was 250 while every request cost Shopify a search;
     the answer now comes from the server's own memory in a few milliseconds. */
  var DEBOUNCE = 90;
  var LIMIT = 12;
  var ENDPOINT = 'https://api.truclothing.com/api/catalog/search/suggest';

  function init(root) {
    var input = root.querySelector('[data-tc-hsearch-input]');
    var panel = root.querySelector('[data-tc-hsearch-panel]');
    var suggest = root.querySelector('[data-tc-hsuggest]');
    var form = root.querySelector('form');
    if (!input || !panel || !form) return;

    /*
     * Two panels share the space under the bar. The suggestions are already
     * in the page and show the instant it is clicked — before a letter is
     * typed there is nothing to wait for, and a box that opens empty is the
     * slowest possible answer to somebody who wants to buy something. As soon
     * as there is a term, the results take over; clear it and the suggestions
     * come back.
     */
    /*
     * The panel is rendered hidden, and an image inside a hidden subtree is
     * never fetched. Chrome does not reliably reconsider those once it is
     * shown, so the first open asks for them explicitly — the panel is at
     * most a handful of pictures and they are the whole point of it. Done
     * once, on demand, so a visitor who never opens the search never pays
     * for them.
     */
    var warmed = false;
    function warmImages() {
      if (warmed || !suggest) return;
      warmed = true;
      [].slice.call(suggest.querySelectorAll('img[loading="lazy"]')).forEach(function (img) {
        img.loading = 'eager';
        if (!img.complete) img.src = img.src;
      });
    }

    function showSuggest() {
      if (!suggest) return false;
      warmImages();
      suggest.hidden = false;
      root.classList.remove('tc-hsearch--open');
      input.setAttribute('aria-expanded', 'true');
      return true;
    }
    function hideSuggest() {
      if (suggest) suggest.hidden = true;
    }

    var timer = null;
    var logTimer = null;
    var controller = null;
    var lastTerm = '';
    var lastCount = null;
    var activeIndex = -1;

    function options() {
      return [].slice.call(panel.querySelectorAll('[role="option"]'));
    }

    function close() {
      root.classList.remove('tc-hsearch--open');
      hideSuggest();
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function open() {
      hideSuggest();
      root.classList.add('tc-hsearch--open');
      input.setAttribute('aria-expanded', 'true');
    }

    function mark(i) {
      var opts = options();
      if (!opts.length) return;
      activeIndex = Math.max(-1, Math.min(i, opts.length - 1));
      opts.forEach(function (o) { o.classList.remove('tc-hsearch--active'); });
      if (activeIndex < 0) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      var el = opts[activeIndex];
      el.classList.add('tc-hsearch--active');
      input.setAttribute('aria-activedescendant', el.id);
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    /*
     * Our own search, not Shopify's predictive.
     *
     * Predictive matches titles strictly and it shows: "navy suit" returned
     * two products out of a catalogue with three hundred and twenty-nine
     * three-piece suits in it, and the panel filled the gap with a collection
     * and a page. The same term through our own search returns twelve. A
     * customer typing two words that describe a suit should be looking at
     * suits.
     */
    /*
     * The shop sells in five languages, and the search has to be asked in the
     * one the shopper is reading. Shopify's own search follows the locale it is
     * served under; ours is a separate API and has to be told, or a French
     * shopper searching "chaussures" is asking an English index a French
     * question.
     */
    var LANG = root.getAttribute('data-lang') || '';
    var COUNTRY = root.getAttribute('data-country') || '';
    var CURRENCY = root.getAttribute('data-currency') || '';

    /*
     * The rail is desktop only, and built rather than hidden.
     *
     * A phone's panel is the width of the phone, and a column of collection
     * names under twelve products is a scroll nobody finishes — it was right as
     * it was. Hiding it in CSS would still have left the "Products" heading
     * above the rows and the links in the keyboard's list of options, so it is
     * simply not made at all below the width that has room for it.
     */
    function wide() {
      return !window.matchMedia || window.matchMedia('(min-width: 1100px)').matches;
    }


    /* Shopify's translation filter HTML-escapes every value, which is right
       for text in the page and wrong for words handed to a script: "&" arrives
       as "&amp;" and a <b> as "&lt;b&gt;". Undone here, once, on the way in. */
    function unescapeLabels(obj) {
      var out = {};
      for (var k in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
        var v = obj[k];
        out[k] = typeof v === 'string'
          ? v.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
          : v;
      }
      return out;
    }
    var L = {};
    try { L = unescapeLabels(JSON.parse(root.getAttribute('data-labels') || '{}')); } catch (e) { L = {}; }
    var ROOT = root.getAttribute('data-root') || '/';

    /* Links have to stay inside the locale they were clicked in: on /fr a bare
       /products/... is the English shop. */
    function localised(path) {
      return (ROOT === '/' ? '' : ROOT.replace(/\/$/, '')) + path;
    }

    function buildUrl(term) {
      var url = new URL(ENDPOINT);
      url.searchParams.set('q', term);
      url.searchParams.set('limit', LIMIT);
      if (LANG) url.searchParams.set('lang', LANG);
      if (COUNTRY) url.searchParams.set('country', COUNTRY);
      return url;
    }

    /* Shopify resizes on the way out, so both sizes the panel uses are asked
       for by name: a 46px thumbnail for the phone's rows and a card for the
       desktop's grid. Left to itself the browser would take the 1600px
       original, which is a megabyte a result. */
    function sized(url, w) {
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }

    /*
     * Priced in whatever the shopper is buying in.
     *
     * The search answers in the market's own currency now that it is told which
     * market — Swedish kronor for Sweden, francs for Switzerland — so the panel
     * must not assume pounds. It formats to the storefront's language as well as
     * its currency, because 40 euros is "40,00 €" to a French shopper and
     * "€40.00" to an English one, and the rest of the page already says it the
     * first way.
     *
     * Whole amounts lose their decimals — £40, not £40.00 — and only whole ones,
     * so a currency that does not use two places is left as Intl has it.
     */
    function money(amount, code) {
      var n = Number(amount);
      if (!isFinite(n)) return '';
      var currency = code || CURRENCY || 'GBP';
      try {
        var opts = { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' };
        if (n % 1 === 0) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
        /* The narrow symbol, because the rest of the page uses it: French names
           sterling "£GB" and Spanish "GBP" by default, and a price beside one
           Liquid rendered as "£89.00" should not be wearing a different badge. */
        try {
          return new Intl.NumberFormat(LANG || 'en-GB', opts).format(n);
        } catch (e2) {
          delete opts.currencyDisplay;
          return new Intl.NumberFormat(LANG || 'en-GB', opts).format(n);
        }
      } catch (e) {
        /* An unknown currency code is still worth naming: a bare number tells a
           shopper nothing, and a pound sign tells them something untrue. */
        return currency + ' ' + n.toFixed(2);
      }
    }

    function heading(text) {
      var h = document.createElement('p');
      h.className = 'tc-hsearch__h';
      h.textContent = text;
      return h;
    }

    /* A collection or a page: a different kind of answer, not a lesser one. */
    function sidelink(href, title, kind) {
      var a = document.createElement('a');
      a.className = 'tc-hsearch__sidelink';
      a.setAttribute('role', 'option');
      a.href = href;
      var t = document.createElement('span');
      t.textContent = title;
      a.appendChild(t);
      if (kind) {
        var k = document.createElement('span');
        k.className = 'tc-hsearch__kind';
        k.textContent = kind;
        a.appendChild(k);
      }
      return a;
    }

    function card(p, id) {
      var row = document.createElement('a');
      row.className = 'tc-hsearch__result';
      row.setAttribute('role', 'option');
      row.id = id;
      row.href = localised('/products/' + p.handle);
      /* Which result was the answer. The ranking is rules about words, and a
         customer choosing one product out of twelve knows something the rules
         do not; enough of them agreeing moves that product up for that term. */
      row.addEventListener('click', function () { logClick(input.value, p.handle); });

      var fig = document.createElement('span');
      fig.className = 'tc-hsearch__thumb';
      if (p.image && p.image.url) {
        var img = document.createElement('img');
        img.src = sized(p.image.url, 420);
        img.srcset = sized(p.image.url, 220) + ' 220w, ' + sized(p.image.url, 420) + ' 420w';
        img.sizes = '(min-width: 1100px) 240px, (min-width: 700px) 220px, 46px';
        img.alt = '';
        img.loading = 'lazy';
        fig.appendChild(img);
      }
      row.appendChild(fig);

      var text = document.createElement('span');
      text.className = 'tc-hsearch__text';
      var t = document.createElement('span');
      t.className = 'tc-hsearch__t';
      t.textContent = p.title;
      text.appendChild(t);

      if (p.price != null) {
        var pr = document.createElement('span');
        pr.className = 'tc-hsearch__p';
        pr.textContent = money(p.price, p.currencyCode);
        /* The old price beside the new one, when there is a real reduction.
           The API has already applied the five-percent floor, so a compare-at
           that arrives here is one worth showing. */
        if (p.compareAtPrice != null && Number(p.compareAtPrice) > Number(p.price)) {
          var was = document.createElement('s');
          was.className = 'tc-hsearch__was';
          was.textContent = money(p.compareAtPrice, p.currencyCode);
          pr.appendChild(document.createTextNode(' '));
          pr.appendChild(was);
        }
        if (p.available === false) pr.appendChild(document.createTextNode(' \u00b7 sold out'));
        text.appendChild(pr);
      }
      row.appendChild(text);
      return row;
    }

    /*
     * Everything the box has to say, in one panel.
     *
     * Products are the answer most of the time, but not all of it: "wedding" is
     * a collection before it is a product and probably a guide as well, and a
     * panel that shows products alone throws those away. They sit in a rail
     * beside the grid rather than under it, where the grid's second row would
     * bury them.
     */
    function render(data, term) {
      var products = (data && data.products) || [];
      var collections = (data && data.collections) || [];
      var pages = (data && data.pages) || [];
      var articles = (data && data.articles) || [];
      var suggested = (data && data.suggestions) || [];
      lastCount = products.length;
      panel.innerHTML = '';

      /* What the search actually asked, when it was not what was typed. */
      if (data && data.corrected) {
        var note = document.createElement('p');
        note.className = 'tc-hsearch__corrected';
        note.textContent = (L.showingFor || 'Showing results for') + ' \u201c' + data.corrected + '\u201d';
        panel.appendChild(note);
      }

      var body = document.createElement('div');
      body.className = 'tc-hsearch__body';

      var rail = document.createElement('div');
      rail.className = 'tc-hsearch__rail';
      var desktop = wide();

      if (desktop && suggested.length) {
        rail.appendChild(heading(L.suggestions || 'Suggestions'));
        var sw = document.createElement('div');
        sw.className = 'tc-hsearch__side';
        suggested.slice(0, 6).forEach(function (word) {
          var url = new URL(form.action, window.location.origin);
          url.searchParams.set('q', word);
          sw.appendChild(sidelink(url.toString(), word, null));
        });
        rail.appendChild(sw);
      }

      if (desktop && collections.length) {
        rail.appendChild(heading(L.collections || 'Collections'));
        var cw = document.createElement('div');
        cw.className = 'tc-hsearch__side';
        collections.slice(0, 5).forEach(function (c) {
          cw.appendChild(sidelink(localised('/collections/' + c.handle), c.title, null));
        });
        rail.appendChild(cw);
      }

      var reading = pages.concat(articles);
      if (desktop && reading.length) {
        rail.appendChild(heading(L.pages || 'Pages & guides'));
        var pw = document.createElement('div');
        pw.className = 'tc-hsearch__side';
        reading.slice(0, 5).forEach(function (item) {
          var href = item.blog
            ? localised('/blogs/' + item.blog + '/' + item.handle)
            : localised('/pages/' + item.handle);
          pw.appendChild(sidelink(href, item.title, null));
        });
        rail.appendChild(pw);
      }

      /* Only when it has something in it: an empty 210px track squeezed the
         products into the rest and left a column of white beside them. */
      var hasRail = !!rail.childNodes.length;
      if (hasRail) {
        body.className += ' tc-hsearch__body--rail';
        body.appendChild(rail);
      }

      var main = document.createElement('div');
      main.className = 'tc-hsearch__main';
      if (products.length) {
        if (hasRail) main.appendChild(heading(L.products || 'Products'));
        var list = document.createElement('div');
        list.className = 'tc-hsearch__list';
        products.slice(0, LIMIT).forEach(function (p, i) {
          list.appendChild(card(p, 'tc-hsearch-r' + i));
        });
        main.appendChild(list);
      } else {
        var none = document.createElement('p');
        none.className = 'tc-hsearch__none';
        none.textContent = L.empty || 'Nothing matched. Try fewer words.';
        main.appendChild(none);
        /* Never only that. A blank panel reads as "we don't sell it", so the
           nearest department — or what sells — goes underneath, said plainly
           to be a suggestion and not a match. */
        var instead = (data && data.fallback) || [];
        if (instead.length) {
          main.appendChild(heading(L.popular || 'You might like'));
          var offered = document.createElement('div');
          offered.className = 'tc-hsearch__list';
          instead.slice(0, LIMIT).forEach(function (p, i) {
            offered.appendChild(card(p, 'tc-hsearch-f' + i));
          });
          main.appendChild(offered);
        }
      }
      body.appendChild(main);
      panel.appendChild(body);

      var all = document.createElement('a');
      all.className = 'tc-hsearch__all';
      var full = new URL(form.action, window.location.origin);
      full.searchParams.set('q', (data && data.corrected) || term);
      full.searchParams.set('type', 'product');
      all.href = full.toString();
      all.textContent = (L.viewAll || 'See all results') + ' \u201c' + term + '\u201d';
      panel.appendChild(all);

      open();
      mark(-1);
    }

    function search(term) {
      if (controller) { controller.abort(); controller = null; }
      controller = new AbortController();
      fetch(buildUrl(term), { signal: controller.signal })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          controller = null;
          // A slow response for a term already typed over is noise.
          if (input.value.trim() !== term) return;
          render(data, term);
          /*
           * Logged a beat after the results land, and only if the term is
           * still what is in the box. Somebody typing "navy suit" passes
           * through "nav" and "navy su" on the way, and those are keystrokes
           * rather than questions - counting them would rank the prefixes of
           * popular words above the words.
           */
          clearTimeout(logTimer);
          logTimer = setTimeout(function () {
            if (input.value.trim() === term) logSearch(term, lastCount);
          }, 1200);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // Leave the plain form to do its job.
          close();
        });
    }

    /* Enter is the clearest statement of intent there is: it is logged even
       if the dropdown never opened, and the count is whatever was known. */
    form.addEventListener('submit', function () {
      logSearch(input.value, lastTerm === input.value.trim() ? lastCount : null);
    });

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(timer);
      if (term.length < MIN_CHARS) {
        lastTerm = '';
        root.classList.remove('tc-hsearch--open');
        activeIndex = -1;
        if (!showSuggest()) close();
        return;
      }
      if (term === lastTerm) { if (panel.children.length) open(); return; }
      lastTerm = term;
      timer = setTimeout(function () { search(term); }, DEBOUNCE);
    });

    function onOpenIntent() {
      if (input.value.trim().length >= MIN_CHARS) {
        if (panel.children.length) open();
        return;
      }
      showSuggest();
    }
    input.addEventListener('focus', onOpenIntent);
    input.addEventListener('click', onOpenIntent);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); return; }
      var opts = options();
      if (!opts.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); mark(activeIndex + 1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); mark(activeIndex - 1); }
      if (e.key === 'Enter' && activeIndex >= 0) {
        /* The result is the link, not something wrapping one. Looking inside
           it found nothing, so Enter on a highlighted product fell through to
           the form and went to the results page instead of to the product. */
        var el = opts[activeIndex];
        var link = el.tagName === 'A' ? el : el.querySelector('a');
        if (link) { e.preventDefault(); link.click(); }
      }
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });
    root.addEventListener('focusout', function () {
      // A click on a result moves focus before the click lands.
      setTimeout(function () {
        var a = document.activeElement;
        /*
         * Putting the keyboard away is not leaving the search.
         *
         * Tapping done on the iOS keyboard blurs the field to the body, and
         * closing on that shut the panel at the exact moment the customer had
         * made room to read it - they could see the results or reach them,
         * never both. Focus landing on nothing means the keyboard went down;
         * focus landing on something else is a real move away, and only that
         * closes it. A tap outside still closes it, which is the gesture
         * people actually use.
         */
        if (!a || a === document.body || a === document.documentElement) return;
        if (!root.contains(a)) close();
      }, 120);
    });
  }

  /*
   * The pair of top picks. Only ever two, so this is a swap rather than a
   * carousel: no track, no snapping, nothing to scroll. It runs only while
   * the suggestions are actually on screen — a timer ticking behind a closed
   * panel is work nobody asked for — and stops while the pointer is over it,
   * so a pick cannot change under the hand about to click it.
   */
  function initTop(el) {
    var items = [].slice.call(el.querySelectorAll('[data-tc-htop-item]'));
    if (items.length < 2) return;
    var wait = Number(el.getAttribute('data-interval')) || 10000;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var at = 0;
    var timer = null;
    var over = false;

    function show(i) {
      at = i % items.length;
      items.forEach(function (item, n) {
        var on = n === at;
        item.classList.toggle('tc-htop__item--on', on);
        // The pick underneath is still a link; keep it out of the tab order
        // and out of the accessibility tree while it is not the one showing.
        item.setAttribute('aria-hidden', on ? 'false' : 'true');
        item.tabIndex = on ? 0 : -1;
      });
    }
    show(0);
    function start() {
      if (timer || over || reduced) return;
      timer = setInterval(function () { show(at + 1); }, wait);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    el.addEventListener('pointerenter', function () { over = true; stop(); });
    el.addEventListener('pointerleave', function () { over = false; start(); });

    var panel = el.closest('[data-tc-hsuggest]');
    if (panel && window.MutationObserver) {
      new MutationObserver(function () {
        if (panel.hidden) stop(); else start();
      }).observe(panel, { attributes: true, attributeFilter: ['hidden'] });
      if (!panel.hidden) start();
    } else {
      start();
    }
  }

  /**
   * Tell the shop what was searched for.
   *
   * Shopify answers this search inside the theme, so nothing about it reaches
   * our own records - the most direct statement of demand there is, a person
   * naming what they want in their own words, has been thrown away on every
   * visit. Sent once the customer has settled on a term rather than on every
   * keystroke, and with the number of results, because a term that returns
   * nothing is the row worth reading.
   *
   * Deliberately silent: a search must not break because the counting did.
   */
  var logged = {};
  function logSearch(term, results) {
    var key = String(term || '').trim().toLowerCase();
    if (key.length < 2 || logged[key]) return;
    logged[key] = true;
    try {
      fetch('https://api.truclothing.com/api/catalog/search-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ term: term, results: results, source: 'web' }),
        keepalive: true,
      }).catch(function () {});
    } catch (e) { /* nothing here is worth interrupting a search for */ }
  }

  var CHOSEN = 'tc_search_chosen';

  function logClick(term, handle, kind) {
    var key = String(term || '').trim();
    if (key.length < 3 || !handle) return;
    try {
      fetch('https://api.truclothing.com/api/catalog/search-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ term: key, handle: handle, source: 'web', kind: kind || 'click' }),
        keepalive: true,
      }).catch(function () {});
    } catch (e) { /* a click must never wait on the counting */ }
    /* Remembered for this visit, so that if the product goes in the basket the
       search that led to it can be told it was right. */
    if (!kind) {
      try {
        sessionStorage.setItem(CHOSEN, JSON.stringify({ term: key, handle: handle, at: Date.now() }));
      } catch (e) { /* private windows have no storage; the click still counted */ }
    }
  }

  /*
   * Opening a result is curiosity as often as interest. Adding it to the
   * basket is the customer saying the search found what they came for, and it
   * is weighed several times a click. Said once per product, and only on the
   * page of the product that was chosen from the search.
   */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !/\/cart\/add/.test(String((form.getAttribute && form.getAttribute('action')) || ''))) return;
    try {
      var chosen = JSON.parse(sessionStorage.getItem(CHOSEN) || 'null');
      if (!chosen || Date.now() - chosen.at > 30 * 60 * 1000) return;
      if (window.location.pathname.indexOf('/products/' + chosen.handle) < 0) return;
      sessionStorage.removeItem(CHOSEN);
      logClick(chosen.term, chosen.handle, 'cart');
    } catch (err) { /* the basket must never wait on the counting */ }
  }, true);

  function boot() {
    [].slice.call(document.querySelectorAll('[data-tc-hsearch]')).forEach(init);
    [].slice.call(document.querySelectorAll('[data-tc-htop]')).forEach(initTop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    [].slice.call(e.target.querySelectorAll('[data-tc-hsearch]')).forEach(init);
    [].slice.call(e.target.querySelectorAll('[data-tc-htop]')).forEach(initTop);
  });
})();
